import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { ToastSurface } from "@/components/ui/ToastSurface";
import { useAuth } from "@/contexts/AuthContext";
import type { AccessDenial } from "@/lib/access-denied";

/**
 * Shown when the sign-in worked but this account cannot use the pilot: either
 * it is not admitted, or its address is not verified yet.
 *
 * Signing in successfully and being allowed to use the application are
 * different things, and the gap between them is confusing enough without an
 * application that half loads. Access is granted rather than requested during
 * the pilot, so the honest thing to say is that the account is not on the
 * list, not that something went wrong.
 *
 * An unverified address gets its own message, because a brand-new sign-up has
 * usually just not opened the verification mail, and "not on the list" would
 * send them to support for nothing. Reloading after verifying is enough: the
 * API asks the provider again on every request until it hears a yes.
 *
 * Signing out matters more than it looks: the provider holds the session, so
 * without it somebody who signed in with the wrong account of two has no way
 * back to the other one. This page is a root of its own rather than a page
 * inside the workflow, which is why it mounts its own toast surface.
 */
export function NotAuthorisedPage({ denial }: { denial: AccessDenial }) {
  const { user, logout } = useAuth();
  const unverified = denial === "email-unverified";

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <ToastSurface />
      <Panel pad="none" className="w-full max-w-md">
        <div className="flex flex-col space-y-1.5 p-4 pb-0">
          <div className="font-semibold leading-none tracking-tight">
            {unverified
              ? "Verify your email address"
              : "You do not have access"}
          </div>
          <p className="text-sm text-muted-foreground">
            {unverified
              ? "Open the verification link we sent when this account was created, then reload this page."
              : "Patent Studio is in a closed pilot, and this account is not on the list."}
          </p>
        </div>
        <div className="space-y-4 p-4">
          {user?.email && (
            <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              Signed in as <span className="font-medium">{user.email}</span>
            </p>
          )}
          {unverified ? (
            <Button className="w-full" onClick={() => window.location.reload()}>
              Reload
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              If you should have access, ask the Patent Studio team to add this
              address. If you have another account, sign out and use that one.
            </p>
          )}
          <Button variant="secondary" className="w-full" onClick={logout}>
            Sign out
          </Button>
        </div>
      </Panel>
    </div>
  );
}
